/** Configuration — Quote Matching & Distribution — rules, options, and sample data */

import { buildSampleTradeCategories } from '../sections/AdminDashboard/TradeCategories/tradeCategoriesDummyData';

export const QUOTE_MATCHING_DISTRIBUTION_ROUTE = '/admin/configuration/quote-matching-distribution';

export const CONDITION_FIELD = 'trade_category';
export const CONDITION_FIELD_LABEL = 'Trade Category';

export const CONDITION_OPERATOR_OPTIONS = [
  { value: 'equals', label: 'Equals' },
  { value: 'does_not_equal', label: 'Does Not Equal' },
];

export const RULE_STATUS_EDIT_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

export const EFFECTIVE_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'review_required', label: 'Review Required' },
];

export const RADIUS_OPTIONS = [
  { value: '10', label: '10 km' },
  { value: '20', label: '20 km' },
  { value: '30', label: '30 km' },
  { value: '40', label: '40 km' },
  { value: '50', label: '50 km' },
];

export const QUOTE_INTENT_SOURCES = [
  { value: 'trade_category', label: 'Saved Trade Category' },
  { value: 'subcategory', label: 'Subcategory' },
  { value: 'public_facing_name', label: 'Public-Facing Name' },
  { value: 'keywords', label: 'Keywords (active definition NOT DEFINED)' },
  { value: 'quote_title', label: 'Quote Title' },
  { value: 'job_description', label: 'Job Description' },
];

export const INTENT_AND_ELIGIBILITY_SETTINGS = [
  { key: 'exactTradeCategory', label: 'Exact Trade Category', value: 'Same saved Trade Category for Quote Request and Business' },
  {
    key: 'intentSources',
    label: 'Intent Sources',
    value: QUOTE_INTENT_SOURCES.map((s) => s.label).join(', '),
  },
  {
    key: 'keywordMatching',
    label: 'Keyword Matching',
    value: 'Saved Keywords and their Trade Category / Subcategory relationships; active Keyword definition NOT DEFINED',
  },
  {
    key: 'specificInterest',
    label: 'Specific Intent',
    value: 'Resolved Subcategory must be one of the Business’s saved Quote Interests',
  },
  {
    key: 'generalRequests',
    label: 'General Intent',
    value: 'Accept General Quote Requests must be Yes',
  },
  {
    key: 'interestSource',
    label: 'Business Preferences',
    value: 'FORM-000006 — Business Profile Form',
  },
  { key: 'unclearIntent', label: 'Unclear Intent', value: 'Hold for clarification; do not distribute' },
  { key: 'publishedPostRequired', label: 'Published Post Required', value: 'Off' },
  { key: 'postLocation', label: 'Post Location', value: 'Off' },
  { key: 'featuredStatus', label: 'Featured Status', value: 'Off' },
  { key: 'privateDataExclusion', label: 'Private Data Exclusion', value: 'On' },
];

export const MANDATORY_ELIGIBILITY_CHECKS = [
  { value: 'trade_category', label: 'Trade Category', requiredResult: 'Exact saved Trade Category match.' },
  {
    value: 'quote_interest',
    label: 'Quote Interest',
    requiredResult: 'Specific or general intent eligibility.',
  },
  { value: 'quote_acceptance', label: 'Quote Acceptance', requiredResult: 'Accept Quote Requests is Yes.' },
  {
    value: 'account',
    label: 'Account',
    requiredResult: 'Eligible Business account (definition NOT DEFINED).',
  },
  { value: 'mobile', label: 'Mobile', requiredResult: 'Verification completed.' },
  {
    value: 'restrictions',
    label: 'Restrictions',
    requiredResult: 'No restriction prevents Quote participation (definition NOT DEFINED).',
  },
  {
    value: 'plan',
    label: 'Plan',
    requiredResult: 'Effective plan is Basic or Pro and provides Quote benefits.',
  },
  {
    value: 'compliance',
    label: 'Compliance',
    requiredResult: 'Applicable required compliance checks are satisfied (definition NOT DEFINED).',
  },
  {
    value: 'location',
    label: 'Location',
    requiredResult: 'Within selected radius per Business Location rules.',
  },
  {
    value: 'capacity',
    label: 'Capacity',
    requiredResult: 'At least one Quote remains in the Business’s effective allowance.',
  },
  {
    value: 'block',
    label: 'Block',
    requiredResult: 'No Customer–Business Block prevents the Quote Request.',
  },
  {
    value: 'prior_delivery',
    label: 'Prior Delivery',
    requiredResult: 'The Quote Request has not already been delivered successfully to this Business.',
  },
];

export const BUSINESS_LOCATION_SETTINGS = [
  { key: 'jobLocation', label: 'Job Location', value: 'Quote Request State, Suburb and Postcode' },
  { key: 'businessLocation', label: 'Business Location', value: 'Business Profile Suburb and Postcode, resolved geographically' },
  { key: 'defaultRadius', label: 'Default Radius', value: '10 km' },
  { key: 'radiusOptions', label: 'Radius Options', value: '10, 20, 30, 40 or 50 km' },
  { key: 'sameSuburb', label: 'Same Suburb', value: 'Same resolved geographic suburb including State' },
  { key: 'radiusExpansion', label: 'Radius Expansion', value: 'Off; boundary inclusive' },
  {
    key: 'distanceSource',
    label: 'Distance Source',
    value: 'Approved Location Integration (identity and calculation NOT CONFIRMED)',
  },
  {
    key: 'locationFailure',
    label: 'Location Failure',
    value: 'Missing or invalid values leave the assessment incomplete',
  },
];

export const SELECTION_ORDER = [
  'Same resolved suburb as the job first.',
  'Shorter distance within the selected radius.',
  'Lower unanswered count within Quote History.',
  'Permanent Business ID, ascending.',
];

export const ALLOCATION_SETTINGS = [
  { key: 'maximumBusinesses', label: 'Maximum Businesses', value: '5' },
  { key: 'proPositions', label: 'Pro Positions', value: '3' },
  { key: 'basicPositions', label: 'Basic Positions', value: '1' },
  { key: 'eitherPlanPositions', label: 'Either-Plan Positions', value: '1' },
  {
    key: 'sendMethod',
    label: 'Sending',
    value: 'All selected Businesses at the same time, immediately after final checks',
  },
  {
    key: 'recipientPrivacy',
    label: 'Recipient Privacy',
    value: 'A Business cannot see other recipients or their plans, allocations or replies',
  },
];

export const FINAL_CHECK_OUTCOMES = [
  { situation: 'Unclear or conflicting intent', result: 'Hold for clarification.' },
  {
    situation: 'Required location, selection or eligibility facts unavailable or invalid',
    result: 'Do not distribute until the assessment can complete.',
  },
  { situation: 'No applicable Rule', result: 'Do not distribute or substitute another Rule.' },
  {
    situation: 'Complete assessment finds no eligible Business',
    result: 'Return no eligible match and preserve the Quote Request.',
  },
  {
    situation: 'One to five eligible Businesses',
    result: 'Allocate and send to the final group.',
  },
  {
    situation: 'Some or all deliveries fail',
    result: 'Preserve successes and failures with the fixed group.',
  },
];

/** Saved Trade Categories selectable for Conditions. */
export const QUOTE_MATCHING_TRADE_CATEGORIES = buildSampleTradeCategories().map((c) => ({
  id: c.id,
  name: c.name,
  sourceUpdatedOn: '2026-08-06T10:30:00',
}));

export const DEVELOPER_NAME = 'Adeel Khan';

export const MESSAGES = {
  saved: 'Rule saved.',
  updated: 'Rule updated.',
  duplicateName: 'This Rule Name is already used.',
  duplicateCoverage: 'An Active Rule already has this coverage',
  overlap: 'Another Active Rule overlaps these Conditions',
  repeatedCondition: 'This Condition repeats another Condition.',
  redundantCondition: 'This Condition does not change the Rule’s coverage.',
  impossibleConditions: 'These Conditions cannot match the same Trade Category.',
  invalidHistory: 'Enter a whole number of days greater than zero.',
  unavailableReference: 'Review the selected Trade Category.',
  noCoveredCategory: 'These Conditions do not cover a saved Trade Category.',
  staleEdit: 'This Rule has changed. Review the latest saved Rule.',
  permission: 'You do not have permission to save this Rule.',
  checksUnavailable: 'Required checks could not be completed. Try again.',
  saveFailed: 'The Rule could not be saved. Your changes have been kept for review.',
};

export const createEmptyCondition = () => ({
  id: `cond-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  field: CONDITION_FIELD,
  operator: '',
  tradeCategoryId: '',
});

export const createDefaultQuoteMatchingForm = () => ({
  id: '',
  ruleName: '',
  ruleStatus: 'active',
  status: '',
  conditions: [],
  quoteHistoryDays: '',
  changeReason: '',
  createdBy: '',
  createdOn: '',
  updatedBy: '',
  updatedOn: '',
  changeHistory: [],
});

export const labelFromOptions = (options, value) => {
  if (value == null || value === '') return '—';
  return options.find((o) => o.value === value)?.label || value;
};

export const getConditionOperatorLabel = (value) => labelFromOptions(CONDITION_OPERATOR_OPTIONS, value);

export const normalizeQuoteMatchingStatus = (status) => {
  const s = String(status || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (s === 'active') return 'active';
  if (s === 'inactive') return 'inactive';
  if (s === 'review_required' || s === 'reviewrequired') return 'review_required';
  return s || 'inactive';
};

export const getQuoteMatchingStatusLabel = (status) => {
  const n = normalizeQuoteMatchingStatus(status);
  if (n === 'active') return 'Active';
  if (n === 'inactive') return 'Inactive';
  if (n === 'review_required') return 'Review Required';
  return '—';
};

export const getTradeCategoryById = (id) =>
  QUOTE_MATCHING_TRADE_CATEGORIES.find((tc) => tc.id === id) || null;

export const getTradeCategoryName = (id) => getTradeCategoryById(id)?.name || '';

export const formatQuoteMatchingDate = (value) => {
  if (!value || !String(value).trim()) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return value;
  }
};

export const formatQuoteMatchingDateTime = (value) => {
  if (!value || !String(value).trim()) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return value;
  }
};

export const nextQuoteMatchingRuleId = (rules = []) => {
  const max = rules.reduce((acc, rule) => {
    const match = String(rule.id || '').match(/QMD-(\d+)/i);
    if (!match) return acc;
    return Math.max(acc, Number(match[1]));
  }, 0);
  return `QMD-${String(max + 1).padStart(4, '0')}`;
};

export const normalizeConditionOperator = (value) => {
  const opRaw = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (opRaw === 'equals' || opRaw === 'is') return 'equals';
  if (opRaw === 'does_not_equal' || opRaw === 'doesnotequal' || opRaw === 'is_not' || opRaw === 'isnot') {
    return 'does_not_equal';
  }
  return opRaw || '';
};

export const normalizeCondition = (raw = {}) => ({
  id: raw.id || createEmptyCondition().id,
  field: CONDITION_FIELD,
  operator: normalizeConditionOperator(raw.operator),
  tradeCategoryId: String(raw.tradeCategoryId || raw.value || '').trim(),
});

export const normalizeQuoteMatchingForm = (raw = {}) => {
  const base = createDefaultQuoteMatchingForm();
  const conditions = Array.isArray(raw.conditions)
    ? raw.conditions.map(normalizeCondition)
    : [];
  const ruleStatus =
    raw.ruleStatus === 'inactive' || normalizeQuoteMatchingStatus(raw.status) === 'inactive'
      ? 'inactive'
      : 'active';

  return {
    ...base,
    ...raw,
    id: raw.id || '',
    ruleName: raw.ruleName || '',
    ruleStatus,
    status: normalizeQuoteMatchingStatus(raw.status) || '',
    conditions,
    quoteHistoryDays:
      raw.quoteHistoryDays === 0 || raw.quoteHistoryDays
        ? String(raw.quoteHistoryDays)
        : '',
    changeReason: '',
    createdBy: raw.createdBy || '',
    createdOn: raw.createdOn || '',
    updatedBy: raw.updatedBy || raw.lastUpdatedBy || '',
    updatedOn: raw.updatedOn || raw.lastUpdatedOn || '',
    changeHistory: Array.isArray(raw.changeHistory) ? [...raw.changeHistory] : [],
  };
};

export const formatConditionRow = (condition) => {
  const normalized = normalizeCondition(condition);
  if (!normalized.operator || !normalized.tradeCategoryId) return '—';
  const op = getConditionOperatorLabel(normalized.operator);
  const name = getTradeCategoryName(normalized.tradeCategoryId) || normalized.tradeCategoryId;
  const unavailable = !getTradeCategoryById(normalized.tradeCategoryId);
  return `${CONDITION_FIELD_LABEL} · ${op} · ${name}${unavailable ? ' Unavailable' : ''}`;
};

export const formatConditionsDisplay = (conditions = []) => {
  if (!conditions.length) return 'No Conditions';
  return conditions.map(formatConditionRow).join(' AND ');
};

export const formatQuoteHistoryDisplay = (days) => {
  const n = Number(days);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${n} days`;
};

/** Distinct Trade Category IDs referenced in Conditions (including exclusions). */
export const getReferencedCategoryIds = (conditions = []) => {
  const ids = [];
  const seen = new Set();
  conditions.forEach((c) => {
    const id = String(c.tradeCategoryId || '').trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  });
  return ids;
};

/**
 * Covered Trade Category IDs for a Condition set.
 * Returns { coveredIds, impossible, incompleteRows, unavailableRefs }.
 */
export const evaluateConditionCoverage = (
  conditions = [],
  allCategories = QUOTE_MATCHING_TRADE_CATEGORIES,
) => {
  const allIds = allCategories.map((c) => c.id);
  const rows = (conditions || []).map(normalizeCondition);
  const incompleteRows = rows.some((r) => !r.operator || !r.tradeCategoryId);
  const unavailableRefs = rows
    .filter((r) => r.tradeCategoryId && !getTradeCategoryById(r.tradeCategoryId))
    .map((r) => r.tradeCategoryId);

  if (!rows.length) {
    return {
      coveredIds: [...allIds],
      impossible: false,
      incompleteRows: false,
      unavailableRefs: [],
      repeated: false,
      redundant: false,
    };
  }

  if (incompleteRows) {
    return {
      coveredIds: [],
      impossible: false,
      incompleteRows: true,
      unavailableRefs,
      repeated: false,
      redundant: false,
    };
  }

  const equalsRows = rows.filter((r) => r.operator === 'equals');
  const notEqualRows = rows.filter((r) => r.operator === 'does_not_equal');
  const equalsIds = equalsRows.map((r) => r.tradeCategoryId);
  const notEqualIds = new Set(notEqualRows.map((r) => r.tradeCategoryId));

  const signatures = rows.map((r) => `${r.operator}:${r.tradeCategoryId}`);
  const repeated = new Set(signatures).size !== signatures.length;

  // Equals A AND Does Not Equal A
  const impossibleSame = equalsIds.some((id) => notEqualIds.has(id));
  // Multiple Equals
  const multipleEquals = new Set(equalsIds).size > 1;
  // Equals plus extra Does Not Equal for another category is redundant
  const redundant = equalsIds.length === 1 && notEqualRows.some((r) => r.tradeCategoryId !== equalsIds[0]);

  if (repeated || impossibleSame || multipleEquals) {
    return {
      coveredIds: [],
      impossible: true,
      incompleteRows: false,
      unavailableRefs,
      repeated,
      redundant: false,
    };
  }

  if (redundant) {
    return {
      coveredIds: [],
      impossible: false,
      incompleteRows: false,
      unavailableRefs,
      repeated: false,
      redundant: true,
    };
  }

  if (equalsIds.length === 1) {
    const id = equalsIds[0];
    if (!getTradeCategoryById(id)) {
      return {
        coveredIds: [],
        impossible: false,
        incompleteRows: false,
        unavailableRefs,
        repeated: false,
        redundant: false,
      };
    }
    return {
      coveredIds: [id],
      impossible: false,
      incompleteRows: false,
      unavailableRefs,
      repeated: false,
      redundant: false,
    };
  }

  const coveredIds = allIds.filter((id) => !notEqualIds.has(id));
  return {
    coveredIds,
    impossible: false,
    incompleteRows: false,
    unavailableRefs,
    repeated: false,
    redundant: false,
  };
};

export const findDuplicateRuleName = (rules, ruleName, excludeId) => {
  const name = String(ruleName || '').trim().toLowerCase();
  if (!name) return null;
  return (
    rules.find(
      (r) => r.id !== excludeId && String(r.ruleName || '').trim().toLowerCase() === name,
    ) || null
  );
};

/** Coverage comparisons use Rule Status Active only (includes Review Required Effective Status). */
export const isRuleSelectedActive = (rule) => rule?.ruleStatus === 'active';

export const findOverlappingActiveRule = (
  rules,
  conditions,
  excludeId,
  allCategories = QUOTE_MATCHING_TRADE_CATEGORIES,
) => {
  const current = evaluateConditionCoverage(conditions, allCategories);
  if (current.impossible || current.incompleteRows || !current.coveredIds.length) return null;

  const currentSet = new Set(current.coveredIds);
  const activeRules = (rules || []).filter(
    (r) => r.id !== excludeId && isRuleSelectedActive(r),
  );

  for (const other of activeRules) {
    const otherCov = evaluateConditionCoverage(other.conditions || [], allCategories);
    if (otherCov.impossible || !otherCov.coveredIds.length) continue;
    const overlap = otherCov.coveredIds.some((id) => currentSet.has(id));
    if (overlap) {
      const exactSame =
        otherCov.coveredIds.length === current.coveredIds.length &&
        otherCov.coveredIds.every((id) => currentSet.has(id));
      return { rule: other, exactSame };
    }
  }
  return null;
};

/**
 * Derive Effective Status from Rule Status + references / coverage / Active overlaps.
 * Blank before first Save (no id).
 */
export const deriveQuoteMatchingDisplayStatus = (
  form,
  allRules = [],
  allCategories = QUOTE_MATCHING_TRADE_CATEGORIES,
) => {
  if (!form?.id) return '';
  if (form.ruleStatus === 'inactive') return 'inactive';

  const coverage = evaluateConditionCoverage(form.conditions || [], allCategories);
  if (coverage.impossible || coverage.incompleteRows || coverage.redundant) return 'review_required';
  if (coverage.unavailableRefs.length) return 'review_required';
  if (!coverage.coveredIds.length) return 'review_required';

  const refs = getReferencedCategoryIds(form.conditions || []);
  if (refs.some((id) => !getTradeCategoryById(id))) return 'review_required';

  const conflict = findOverlappingActiveRule(allRules, form.conditions || [], form.id, allCategories);
  if (conflict) return 'review_required';

  return 'active';
};

export const buildSampleQuoteMatchingRules = () => {
  const plumbing = QUOTE_MATCHING_TRADE_CATEGORIES.find((tc) => tc.name === 'Plumbing');
  const electrical = QUOTE_MATCHING_TRADE_CATEGORIES.find((tc) => tc.name === 'Electrical');

  return [
    {
      ...createDefaultQuoteMatchingForm(),
      id: 'QMD-0001',
      ruleName: 'Plumbing Matching',
      ruleStatus: 'active',
      status: 'active',
      conditions: [
        {
          id: 'cond-sample-1',
          field: CONDITION_FIELD,
          operator: 'equals',
          tradeCategoryId: plumbing?.id || 'TC-001',
        },
      ],
      quoteHistoryDays: '90',
      createdBy: 'Adeel Khan',
      createdOn: '2026-07-18T09:30:00',
      updatedBy: 'Adeel Khan',
      updatedOn: '2026-08-06T13:30:00',
      changeHistory: [],
    },
    {
      ...createDefaultQuoteMatchingForm(),
      id: 'QMD-0002',
      ruleName: 'Other Trades Matching',
      ruleStatus: 'active',
      status: 'active',
      conditions: [
        {
          id: 'cond-sample-2',
          field: CONDITION_FIELD,
          operator: 'does_not_equal',
          tradeCategoryId: plumbing?.id || 'TC-001',
        },
      ],
      quoteHistoryDays: '60',
      createdBy: 'Adeel Khan',
      createdOn: '2026-07-19T10:00:00',
      updatedBy: 'Adeel Khan',
      updatedOn: '2026-08-06T11:00:00',
      changeHistory: [],
    },
    {
      ...createDefaultQuoteMatchingForm(),
      id: 'QMD-0003',
      ruleName: 'Electrical Matching',
      ruleStatus: 'inactive',
      status: 'inactive',
      conditions: [
        {
          id: 'cond-sample-3',
          field: CONDITION_FIELD,
          operator: 'equals',
          tradeCategoryId: electrical?.id || 'TC-002',
        },
      ],
      quoteHistoryDays: '45',
      createdBy: 'Maria Chen',
      createdOn: '2026-07-22T11:00:00',
      updatedBy: 'Maria Chen',
      updatedOn: '2026-08-05T09:00:00',
      changeHistory: [],
    },
  ];
};

export const matchesQuoteMatchingSearch = (rule, query) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const conditionNames = (rule.conditions || [])
    .map((c) => getTradeCategoryName(c.tradeCategoryId))
    .join(' ')
    .toLowerCase();
  return (
    String(rule.id || '').toLowerCase().includes(q) ||
    String(rule.ruleName || '').toLowerCase().includes(q) ||
    conditionNames.includes(q)
  );
};

/** Trade Category filter: include Rules whose Conditions cover it, including Rules without Conditions. */
export const matchesQuoteMatchingFilters = (
  rule,
  { tradeCategoryFilter, statusFilter },
  allCategories = QUOTE_MATCHING_TRADE_CATEGORIES,
) => {
  if (tradeCategoryFilter && tradeCategoryFilter !== 'all') {
    const coverage = evaluateConditionCoverage(rule.conditions || [], allCategories);
    if (!coverage.coveredIds.includes(tradeCategoryFilter)) return false;
  }
  if (statusFilter && statusFilter !== 'all') {
    if (normalizeQuoteMatchingStatus(rule.status) !== statusFilter) return false;
  }
  return true;
};

export const getTradeCategoryFilterOptions = () => [
  { value: 'all', label: 'All Categories' },
  ...QUOTE_MATCHING_TRADE_CATEGORIES.map((tc) => ({ value: tc.id, label: tc.name })),
];

export const getListActionsForRule = () => ['view', 'edit'];
